import React, { useState } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Fab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Chip, // ✅ ADD THIS IMPORT
} from '@mui/material';
import { Save as SaveIcon, Search as SearchIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import DataQualityService from '../services/DataQualityService';
import config from '../config'; // Import your config file
import { useNavigate } from 'react-router-dom';
import { useRole } from '../contexts/RoleContext';

const DataQualityPage = () => {
  // State management
  const [requestId, setRequestId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [fieldConfig, setFieldConfig] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [pushingToExcel, setPushingToExcel] = useState(false); // Add this state variable
  const [selectedMode, setSelectedMode] = useState('data_quality');

  const navigate = useNavigate();
  const userRole = useRole();
  
  // Access control configuration
  const ODM_ADMIN_ACCESS = {
    requiredRole: 'ODM Admin',
    allowedUsers: [
      'akshay.deshmukh@allianz.com',
      'jane.smith@allianz.com',
      'retheesh_olangattu@allianz.com',
      'admin@allianz.com',
      'ALIN-MARIAN.DAVID@ALLIANZ.COM'
      // Add more specific users here as needed
    ]
  };

  // Access check function
  const hasDataQualityAccess = () => {
    const isODMAdmin = userRole.roleName === 'ODM Admin';
    const isSpecificUser = ODM_ADMIN_ACCESS.allowedUsers.includes(userRole.userEmail);
    return isODMAdmin && isSpecificUser;
  };

  // Access control check
  if (!hasDataQualityAccess()) {
    return (
      <Layout>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h5" color="error" gutterBottom>
                Access Denied - Data Quality Management
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                This feature is restricted to ODM Admins with specific authorization.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current user: {userRole.userEmail} ({userRole.roleName})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please contact your administrator for access.
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Layout>
    );
  }

  // Search function
  const handleSearch = async () => {
    if (!requestId.trim()) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await DataQualityService.getRequestDataQuality(parseInt(requestId));

      setRequestData(response.request_data);
      setFieldConfig(response.field_configuration);

      // Initialize form values from new sectioned structure
      const initialValues = {};

      // Request-level fields (pricing section)
      ['current', 'post'].forEach(section => {
        response.field_configuration[section]?.pricing?.forEach(field => {
          initialValues[field.key] = field.current_value;
        });
      });

      // Circuit-level fields
      ['current', 'post'].forEach(section => {
        // Circuit fields
        Object.entries(response.field_configuration[section]?.circuits || {}).forEach(([circuitId, fields]) => {
          fields.forEach(field => {
            const key = `${circuitId}_${field.key}`;
            initialValues[key] = field.current_value;
          });
        });

        // Router fields
        Object.entries(response.field_configuration[section]?.routers || {}).forEach(([circuitId, fields]) => {
          fields.forEach(field => {
            const key = `${circuitId}_${field.key}`;
            initialValues[key] = field.current_value;
          });
        });
      });

      setFormValues(initialValues);
      setHasChanges(false);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Value change handler
  const handleValueChange = (key, value) => {
    setFormValues(prev => ({ ...prev, [key]: value }));
    
    // ✅ FIXED: Always set hasChanges to true when any field changes
    setHasChanges(true);
    
    // Clear any existing errors
    setError(null);
  };

  // Save function
  const handleSave = async () => {
    if (!hasChanges) {
      setError('No changes detected to save');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Transform form values back to API format
      const updateData = {
        circuit_fields: {},
        request_fields: {}
      };

      // Get all request-level field keys
      const requestFieldKeys = new Set();
      ['current', 'post'].forEach(section => {
        fieldConfig[section]?.pricing?.forEach(field => {
          requestFieldKeys.add(field.key);
        });
      });

      // Process form values
      Object.keys(formValues).forEach(key => {
        if (requestFieldKeys.has(key)) {
          // This is a request-level field
          updateData.request_fields[key] = formValues[key];
        } else if (key.includes('_')) {
          // This is a circuit-level field (format: circuitId_fieldKey)
          const parts = key.split('_');
          const circuitId = parts[0];
          const fieldKey = parts.slice(1).join('_'); // Handle keys with underscores

          if (!updateData.circuit_fields[circuitId]) {
            updateData.circuit_fields[circuitId] = {};
          }
          updateData.circuit_fields[circuitId][fieldKey] = formValues[key];
        } else {
          // Fallback to request field
          updateData.request_fields[key] = formValues[key];
        }
      });
      console.log('💾 Saving update data:', updateData);
      
      await DataQualityService.updateRequestDataQuality(parseInt(requestId), updateData);
      
      setHasChanges(false);
      setError(null);
      
      // ✅ Show success message
      alert('✅ Changes saved successfully!');
      
    } catch (err) {
      setError(`Save failed: ${err.message}`);
      console.error('❌ Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Push to Excel function
  const handlePushToTempFile = async () => {
    if (!requestId) {
      setError('Please search for a request first');
      return;
    }
    
    try {
      setPushingToExcel(true);
      setError(null);
      
      const response = await fetch(`${config.apiUrl}/api/data-quality/${requestId}/push-to-excel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(`✅ Successfully pushed data to Temp File/ODM Database! Updated ${result.result.updated_circuits} circuits.`);
        console.log('📊 Push to temp file result:', result.result);
      } else {
        setError(`Push to temp file failed: ${result.error}`);
      }
      
    } catch (err) {
      setError(`Push to temp file failed: ${err.message}`);
      console.error('❌ Push to temp file error:', err);
    } finally {
      setPushingToExcel(false);
    }
  };

  // Mode change handler
  const handleModeChange = (event) => {
    const newMode = event.target.value;
    setSelectedMode(newMode);
    
    if (newMode === 'inventory_management') {
      navigate('/inventory-management');
    }
    // If it's 'data_quality', we stay on this page
  };

  // Render field function
  const renderField = (field, isCircuitField = false, isPricing = false) => {
    const key = isCircuitField ? `${field.circuit_id}_${field.key}` : field.key;
    const value = formValues[key] || '';

    if (!field.applicable) return null;

    const fieldProps = {
      fullWidth: true,
      size: 'small',
      value,
      onChange: (e) => handleValueChange(key, e.target.value),
      disabled: field.read_only,
    };

    let fieldComponent;

    if (field.type === 'select') {
      fieldComponent = (
        <FormControl {...fieldProps}>
          <InputLabel>{field.label}</InputLabel>
          <Select {...fieldProps} label={field.label}>
            {field.options?.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    } else {
      fieldComponent = (
        <TextField
          {...fieldProps}
          label={field.label}
          type={field.type === 'number' ? 'number' : 'text'}
          placeholder={field.validation?.max_length ? `Max ${field.validation.max_length} characters` : undefined}
          helperText={field.required ? 'Required field' : undefined}
        />
      );
    }

    return (
      <Grid item xs={12} sm={isPricing ? 12 : 6} md={isPricing ? 6 : 4} key={key}>
        <Box>
          {fieldComponent}
        </Box>
      </Grid>
    );
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Search Section */}
        <Card elevation={3} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ color: '#003781', fontWeight: 'bold' }}>
              Data Management Portal
            </Typography>
            
            {/* Mode Selection Dropdown */}
            <Box sx={{ mb: 3 }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Select Mode</InputLabel>
                <Select
                  value={selectedMode}
                  onChange={handleModeChange}
                  label="Select Mode"
                >
                  <MenuItem value="data_quality">Data Quality</MenuItem>
                  <MenuItem value="inventory_management">Inventory Check/Update</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {/* Only show search if in data quality mode */}
            {selectedMode === 'data_quality' && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                <TextField
                  label="Request ID"
                  value={requestId}
                  onChange={(e) => setRequestId(e.target.value)}
                  placeholder="Enter request ID (e.g., 123)"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading || !requestId.trim()}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ backgroundColor: '#003781' }}
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
                
                <Button
                  variant="contained"
                  onClick={handlePushToTempFile}
                  disabled={loading || pushingToExcel || !requestData}
                  startIcon={pushingToExcel ? <CircularProgress size={20} /> : <SaveIcon />}
                  sx={{ backgroundColor: '#e65100', '&:hover': { backgroundColor: '#bf360c' } }}
                >
                  {pushingToExcel ? 'Pushing...' : 'Push to Temp File/ODM Database'}
                </Button>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )}
          </CardContent>
        </Card>

        {/* Request Overview */}
        {requestData && (
          <Card elevation={3} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: '#003781', mb: 2 }}>
                Request: {requestData.basic_info?.odm_title}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2">
                    <strong>Type:</strong> {requestData.basic_info?.request_type}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2">
                    <strong>Site:</strong> {requestData.site_details?.site_id}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2">
                    <strong>Circuits:</strong> {requestData.network_details?.circuits?.length || 0}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Current Implementation Section */}
        {fieldConfig && (
          <Card elevation={3} sx={{ mb: 3, backgroundColor: '#e3f2fd' }}>
            <CardContent>
              <Typography variant="h5" sx={{ color: '#1565c0', mb: 3, fontWeight: 'bold' }}>
                Current Implementation (Reference)
              </Typography>

              {/* Current Pricing Section */}
              {fieldConfig.current?.pricing && fieldConfig.current.pricing.length > 0 && (
                <Card elevation={2} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#1976d2', mb: 2 }}>
                      Pricing
                    </Typography>
                    <Grid container spacing={2}>
                      {fieldConfig.current.pricing.map(field => renderField(field, false, true))}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Current Circuit-level Fields */}
              {requestData?.network_details?.circuits?.map((circuit) => {
                const circuitId = circuit.circuit_id;
                const circuitFields = fieldConfig.current?.circuits?.[circuitId] || [];
                const routerFields = fieldConfig.current?.routers?.[circuitId] || [];

                if (circuitFields.length === 0 && routerFields.length === 0) return null;

                return (
                  <Card elevation={2} sx={{ mb: 2 }} key={`current-${circuitId}`}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: '#1976d2' }}>
                          Circuit: {circuitId}
                        </Typography>
                        <Chip
                          label={`${circuit.current_bandwidth} → ${circuit.new_bandwidth} Mbps`}
                          variant="outlined"
                        />
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      {/* Circuit Fields */}
                      {circuitFields.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ color: '#1e88e5', mb: 1 }}>
                            Circuit Details
                          </Typography>
                          <Grid container spacing={2}>
                            {circuitFields.map(field => renderField(field, true))}
                          </Grid>
                        </Box>
                      )}

                      {/* Router Fields */}
                      {routerFields.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#1e88e5', mb: 1 }}>
                            Router Details
                          </Typography>
                          <Grid container spacing={2}>
                            {routerFields.map(field => renderField(field, true))}
                          </Grid>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Post Implementation Section */}
        {fieldConfig && (
          <Card elevation={3} sx={{ mb: 3, backgroundColor: '#e8f5e9' }}>
            <CardContent>
              <Typography variant="h5" sx={{ color: '#2e7d32', mb: 3, fontWeight: 'bold' }}>
                Post Implementation (Editable)
              </Typography>

              {/* Post Pricing Section */}
              {fieldConfig.post?.pricing && fieldConfig.post.pricing.length > 0 && (
                <Card elevation={2} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#2e7d32', mb: 2 }}>
                      Pricing
                    </Typography>
                    <Grid container spacing={2}>
                      {fieldConfig.post.pricing.map(field => renderField(field, false, true))}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Post Circuit-level Fields */}
              {requestData?.network_details?.circuits?.map((circuit) => {
                const circuitId = circuit.circuit_id;
                const circuitFields = fieldConfig.post?.circuits?.[circuitId] || [];
                const routerFields = fieldConfig.post?.routers?.[circuitId] || [];

                if (circuitFields.length === 0 && routerFields.length === 0) return null;

                return (
                  <Card elevation={2} sx={{ mb: 2 }} key={`post-${circuitId}`}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: '#2e7d32' }}>
                          Circuit: {circuitId}
                        </Typography>
                        <Chip
                          label={`${circuit.current_bandwidth} → ${circuit.new_bandwidth} Mbps`}
                          variant="outlined"
                          color="success"
                        />
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      {/* Circuit Fields */}
                      {circuitFields.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ color: '#388e3c', mb: 1 }}>
                            Circuit Details
                          </Typography>
                          <Grid container spacing={2}>
                            {circuitFields.map(field => renderField(field, true))}
                          </Grid>
                        </Box>
                      )}

                      {/* Router Fields */}
                      {routerFields.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#388e3c', mb: 1 }}>
                            Router Details
                          </Typography>
                          <Grid container spacing={2}>
                            {routerFields.map(field => renderField(field, true))}
                          </Grid>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Floating Save Button */}
        {hasChanges && (
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              backgroundColor: '#003781',
            }}
            onClick={handleSave}
            disabled={loading}
          >
            <SaveIcon />
          </Fab>
        )}
      </Container>
    </Layout>
  );
};

export default DataQualityPage;